import FileInput from "./FileInput";
import FileItem from "./FileItem";
import m, {Vnode} from "mithril";
import Api from "../../api";
import {TextareaItem} from "../../model/items/textarea";
import {TextboxItem} from "../../model/items/textbox";

export default class Uploader {

    private readonly api: Api;

    private readonly fileInput: FileInput;
    private fileItems: FileItem[] = [];

    private isUploading = false;
    private errorMessage = '';
    private errorFiles: string[] = [];

    private uploadedFileInfo: any[] = [];

    constructor(
        public readonly filesMetadata: TextareaItem,
        public readonly fileType: "image"|"document"|"all" = "all",
        public readonly filesCount: TextboxItem|null = null,
    ) {
        const questionary = filesMetadata.form.questionary;
        this.fileInput = new FileInput(this.getAccept(), this.addFile.bind(this), questionary.messages, filesMetadata);
        this.api = questionary.api;
    }

    private onSuccessUpload(filesInfo:any[]) {
        let oldInfoJson = this.filesMetadata.getVariable();
        let oldInfo;
        try {
            oldInfo = <any[]>JSON.parse(oldInfoJson);
        } catch (error) {
            oldInfo = [];
        }

        this.filesCount?.setVariable(oldInfo.length + filesInfo.length);
        this.filesMetadata.setVariable(JSON.stringify(oldInfo.concat(filesInfo)));
    }

    private getAccept(): string {
        switch (this.fileType) {
            case "image": return 'image/*';
            case "document": return '.xlsx, .xls, image/*, .doc, .docx, .ppt, .pptx, .txt, .pdf';
        }
        return '*';
    }

    private addFile(content: string, file: File): void {
        if (!this.filesMetadata.getVariable()) {
            this.filesMetadata.set('ping global unknown')
            this.filesMetadata.set(undefined);
        }

        for (let i=0; i<this.fileItems.length; i++) {
            if (
                (content && this.fileItems[i].content === content)
                || (!content && this.fileItems[i].file.name === file.name && this.fileItems[i].file.size === file.size)
            ) {
                this.fileItems.splice(i, 1); // remove duplicity
                break;
            }
        }
        this.fileItems.push(new FileItem(
            content,
            file,
            this.deleteFile.bind(this)
        ));
        m.redraw();
    }

    private deleteFile(fileItem: FileItem): void {
        for(let i=0; i<this.fileItems.length; i++) {
            if (this.fileItems[i].content === fileItem.content) {
                this.fileItems.splice(i, 1);
                break;
            }
        }
    }

    public uploadFiles() {
        if (this.fileItems.length === 0) {
            return;
        }

        const data = new FormData();
        while (this.fileItems.length) {
            const fileItem = this.fileItems.shift();
            if (!fileItem) {
                break;
            }
            data.append('files[]', fileItem.file);
        }

        const onSuccess = (data: string) => {
            const json = JSON.parse(data);
            this.errorMessage = '';
            this.errorFiles = [];

            if (!json.error) {
                for (const fileInfo of json.filesInfo) {
                    if (fileInfo.error) {
                        this.errorFiles.push(fileInfo.fileName);
                    } else if (fileInfo.status === "success") {
                        this.uploadedFileInfo.push(fileInfo);
                    }
                }
                this.onSuccessUpload(this.uploadedFileInfo);
            } else {
                this.errorMessage = json.error;
            }

            this.onUploadingFinish();
        };

        const onError = () => {
            this.errorMessage = 'error';
            this.onUploadingFinish();
        };

        this.onUploadingStart();
        this.api.uploadFiles(data).then( onSuccess.bind(this), onError.bind(this));
    }

    public fileCount(): number {
        return this.fileItems.length;
    }

    public getIsUploading(): boolean {
        return this.isUploading;
    }

    public reset(): void {
        this.isUploading = false;
        this.errorMessage = '';
        this.errorFiles = [];
        this.uploadedFileInfo = [];
        this.fileItems = [];
    }

    private onUploadingStart() {
        this.isUploading = true;
        m.redraw();
    }

    private onUploadingFinish() {
        this.isUploading = false;
        m.redraw();
    }

    public view(): Vnode|null {
        if (!this.filesMetadata.cell.visible) {
            return null;
        }

        const questionary = this.filesMetadata.questionary;

        if (this.isUploading) {
            return m('.uploader.isUploading', {}, [
                m('.icon.icon-upload.bounce-7'),
                m('p.hint', {}, questionary.renderText(questionary.messages.get('waitPlease')))
            ]);
        }

        // Warning about filesize
        const filesizeWarning = this.fileItems.reduce((sum,f)=>sum+f.file.size, 0) > 256*1024*1024 ? m('p.error', {}, questionary.renderText(questionary.messages.get('filesExceededMaxSize'))) : null;

        // General error with uploading (server down, etc)
        const errorMessage = this.errorMessage ?  m('p.error', {}, questionary.renderText(questionary.messages.get(this.errorMessage))) : null;

        // Specific files problem
        let errorFileMessage = null;
        if (this.errorFiles.length) {
            errorFileMessage = m('p.error', {},
                questionary.renderText(questionary.messages.get('filesNotUploaded', {files: this.errorFiles.join(', ')}))
            );
        }

        return m('.uploader'+ (this.filesMetadata.cell.enabled ? '' : '.disabled'), {}, [
            m('.uploader-input', {}, [
                m(this.fileInput)
            ]),
            errorMessage,
            filesizeWarning,
            errorFileMessage,
            m('.uploader-files', {}, this.fileItems.map(i => m(i))),
        ]);
    }
}
